import { Link } from 'react-router-dom'
import { ArrowLeft, Shield, Lock, Eye, Database, UserCheck } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-8 px-6 max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center">
              <Shield className="h-8 w-8 text-accent-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight">Privacy Policy</h1>
              <p className="text-muted-foreground mt-1">Last updated: January 15, 2025</p>
            </div>
          </div>
          <Link to="/login">
            <Button variant="outline" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Login
            </Button>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Lock className="h-6 w-6 text-accent-foreground" />
              FeedbackPro Privacy Policy
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">1. Introduction</h2>
              <p className="text-muted-foreground leading-relaxed">
                FeedbackPro ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how
                we collect, use, disclose, and safeguard your information when you use our feedback and performance management platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">2. Information We Collect</h2>

              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
                  <UserCheck className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Personal Information</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We collect information you provide directly, such as:
                    </p>
                    <ul className="list-disc pl-6 text-muted-foreground mt-2 space-y-1">
                      <li>Name, email address, and profile information</li>
                      <li>Job title, department, and organizational role</li>
                      <li>Feedback, goals, and performance data</li>
                      <li>Communication preferences and settings</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-secondary rounded-lg border border-border">
                  <Eye className="h-5 w-5 text-secondary-foreground mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Usage Information</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We automatically collect certain information when you use our Service:
                    </p>
                    <ul className="list-disc pl-6 text-muted-foreground mt-2 space-y-1">
                      <li>Log data, including IP addresses and browser information</li>
                      <li>Device information and operating system details</li>
                      <li>Usage patterns and feature interactions</li>
                      <li>Performance metrics and analytics data</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <Database className="h-5 w-5 text-warning mt-0.5" />
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-2">Cookies and Tracking</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      We use cookies and similar technologies to:
                    </p>
                    <ul className="list-disc pl-6 text-muted-foreground mt-2 space-y-1">
                      <li>Maintain user sessions and preferences</li>
                      <li>Analyze usage patterns and improve our Service</li>
                      <li>Provide personalized experiences</li>
                      <li>Ensure security and prevent fraud</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">3. How We Use Your Information</h2>
              <p className="text-muted-foreground leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Provide, operate, and maintain our Service</li>
                <li>Process feedback, goals, and performance evaluations</li>
                <li>Generate analytics and reports for your organization</li>
                <li>Communicate with you about your account and our Service</li>
                <li>Improve our Service and develop new features</li>
                <li>Ensure security and prevent unauthorized access</li>
                <li>Comply with legal obligations and resolve disputes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">4. Information Sharing and Disclosure</h2>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Within Your Organization</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Your feedback and performance data may be visible to designated managers, HR personnel, and team members as configured by your organization's privacy settings.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Service Providers</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We may share information with trusted third-party service providers who assist us in operating our Service, including hosting, analytics, and customer support providers.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Legal Requirements</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We may disclose information if required by law, legal process, or to protect the rights, property, or safety of FeedbackPro, our users, or others.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-foreground mb-2">Business Transfers</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    In the event of a merger, acquisition, or asset sale, user information may be transferred as part of that transaction.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">5. Data Security</h2>
              <div className="space-y-4">
                <p className="text-muted-foreground leading-relaxed">
                  We implement comprehensive security measures to protect your information:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Encryption in transit and at rest for sensitive data</li>
                  <li>Strict access controls and authentication policies</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Continuous monitoring for suspicious activity</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">6. Data Retention</h2>
              <p className="text-muted-foreground leading-relaxed">
                We retain your information for as long as your account is active or as needed to provide our Service. We may also retain and use your information to comply with our legal obligations, resolve disputes, and enforce our agreements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">7. Your Rights and Choices</h2>
              <p className="text-muted-foreground leading-relaxed">Depending on your location, you may have the right to:</p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>Access and obtain a copy of your personal information</li>
                <li>Request corrections or updates to your information</li>
                <li>Delete your information or restrict its processing</li>
                <li>Object to certain data processing activities</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="text-muted-foreground mt-4">
                To exercise these rights, please contact your organization's administrator or reach out to us directly at privacy@gathio.com.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">8. International Data Transfers</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may transfer your information to countries outside your residence. When we do, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or other lawful mechanisms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">9. Changes to This Policy</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date at the top of this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4">10. Contact Us</h2>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions or concerns about this Privacy Policy, please contact us at privacy@gathio.com or through the contact details provided in your organization's agreement.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default PrivacyPolicyPage
